import type { TFunction } from "i18next";

// Official WhatsApp Cloud API error codes:
// https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes
export const WABA_ERROR_CODES = {
  // Throttling / delivery caps
  API_TOO_MANY_CALLS: 4,
  APP_RATE_LIMIT: 80007,
  CLOUD_API_RATE_LIMIT: 130429,
  SPAM_RATE_LIMIT: 131048,
  PAIR_RATE_LIMIT: 131056,
  HEALTHY_ECOSYSTEM_ENGAGEMENT: 131049,

  // Integrity
  TEMPORARILY_BLOCKED_POLICY: 368,
  ACCOUNT_LOCKED: 131031,

  // Media errors
  MEDIA_DOWNLOAD_ERROR: 131052,
  MEDIA_UPLOAD_ERROR: 131053,

  // Template errors
  TEMPLATE_PARAM_COUNT_MISMATCH: 132000,
  TEMPLATE_NOT_FOUND: 132001,
  TEMPLATE_TEXT_TOO_LONG: 132005,
  TEMPLATE_CONTENT_POLICY_VIOLATED: 132007,
  TEMPLATE_PARAMS_INVALID: 132012,
  TEMPLATE_PAUSED: 132015,
  TEMPLATE_DISABLED: 132016,

  // Recipient errors
  RECIPIENT_CANNOT_BE_SENDER: 131021,
  MESSAGE_UNDELIVERABLE: 131026,
  RECIPIENT_NOT_IN_ALLOWED_LIST: 131030,
  USER_EXPERIMENT: 130472,
  OUTSIDE_24H_WINDOW: 131047,

  // System / account errors
  INTERNAL_ERROR: 131000,
  ACCESS_DENIED: 131005,
  SERVICE_UNAVAILABLE: 131016,
  PAYMENT_ISSUE: 131042,
  INCORRECT_CERTIFICATE: 131045,
  ACCOUNT_IN_MAINTENANCE_MODE: 131057,

  // Message errors
  UNSUPPORTED_MESSAGE_TYPE: 131051,

  // API parameter errors
  INVALID_PARAMETER: 100,
  REQUIRED_PARAMETER_MISSING: 131008,
  PARAMETER_VALUE_INVALID: 131009,

  // Registration errors
  PHONE_NUMBER_REVERIFICATION_NEEDED: 133006,
  PHONE_NUMBER_NOT_REGISTERED: 133010,
} as const;

export const WABA_ERROR_TRANSLATION_KEYS: Record<number, string> = {
  // Throttling / delivery caps
  [WABA_ERROR_CODES.API_TOO_MANY_CALLS]: "waba_errors.rate_limit_hit",
  [WABA_ERROR_CODES.APP_RATE_LIMIT]: "waba_errors.rate_limit_hit",
  [WABA_ERROR_CODES.CLOUD_API_RATE_LIMIT]: "waba_errors.cloud_api_rate_limit",
  [WABA_ERROR_CODES.SPAM_RATE_LIMIT]: "waba_errors.message_limit_exceeded",
  [WABA_ERROR_CODES.PAIR_RATE_LIMIT]: "waba_errors.pair_rate_limit",
  [WABA_ERROR_CODES.HEALTHY_ECOSYSTEM_ENGAGEMENT]: "waba_errors.healthy_ecosystem_engagement",

  // Integrity
  [WABA_ERROR_CODES.TEMPORARILY_BLOCKED_POLICY]: "waba_errors.business_restricted",
  [WABA_ERROR_CODES.ACCOUNT_LOCKED]: "waba_errors.account_locked",

  // Media errors
  [WABA_ERROR_CODES.MEDIA_DOWNLOAD_ERROR]: "waba_errors.media_download_failed",
  [WABA_ERROR_CODES.MEDIA_UPLOAD_ERROR]: "waba_errors.media_upload_failed",

  // Template errors
  [WABA_ERROR_CODES.TEMPLATE_PARAM_COUNT_MISMATCH]: "waba_errors.template_param_count_mismatch",
  [WABA_ERROR_CODES.TEMPLATE_NOT_FOUND]: "waba_errors.template_not_found",
  [WABA_ERROR_CODES.TEMPLATE_TEXT_TOO_LONG]: "waba_errors.template_text_too_long",
  [WABA_ERROR_CODES.TEMPLATE_CONTENT_POLICY_VIOLATED]: "waba_errors.template_content_policy",
  [WABA_ERROR_CODES.TEMPLATE_PARAMS_INVALID]: "waba_errors.template_params_invalid",
  [WABA_ERROR_CODES.TEMPLATE_PAUSED]: "waba_errors.template_paused",
  [WABA_ERROR_CODES.TEMPLATE_DISABLED]: "waba_errors.template_disabled",

  // Recipient errors
  [WABA_ERROR_CODES.RECIPIENT_CANNOT_BE_SENDER]: "waba_errors.recipient_same_as_sender",
  [WABA_ERROR_CODES.MESSAGE_UNDELIVERABLE]: "waba_errors.invalid_recipient",
  [WABA_ERROR_CODES.RECIPIENT_NOT_IN_ALLOWED_LIST]: "waba_errors.recipient_not_in_allowed_list",
  [WABA_ERROR_CODES.USER_EXPERIMENT]: "waba_errors.user_experiment",
  [WABA_ERROR_CODES.OUTSIDE_24H_WINDOW]: "waba_errors.outside_24h_window",

  // System / account errors
  [WABA_ERROR_CODES.INTERNAL_ERROR]: "waba_errors.internal_error",
  [WABA_ERROR_CODES.ACCESS_DENIED]: "waba_errors.access_denied",
  [WABA_ERROR_CODES.SERVICE_UNAVAILABLE]: "waba_errors.temporarily_unavailable",
  [WABA_ERROR_CODES.PAYMENT_ISSUE]: "waba_errors.payment_issue",
  [WABA_ERROR_CODES.INCORRECT_CERTIFICATE]: "waba_errors.incorrect_certificate",
  [WABA_ERROR_CODES.ACCOUNT_IN_MAINTENANCE_MODE]: "waba_errors.account_maintenance",

  // Message errors
  [WABA_ERROR_CODES.UNSUPPORTED_MESSAGE_TYPE]: "waba_errors.unsupported_message",

  // API parameter errors
  [WABA_ERROR_CODES.INVALID_PARAMETER]: "waba_errors.parameter_value_invalid",
  [WABA_ERROR_CODES.REQUIRED_PARAMETER_MISSING]: "waba_errors.required_parameter_missing",
  [WABA_ERROR_CODES.PARAMETER_VALUE_INVALID]: "waba_errors.parameter_value_invalid",

  // Registration errors
  [WABA_ERROR_CODES.PHONE_NUMBER_REVERIFICATION_NEEDED]: "waba_errors.phone_number_reverification_needed",
  [WABA_ERROR_CODES.PHONE_NUMBER_NOT_REGISTERED]: "waba_errors.phone_number_not_registered",
};

export function getWabaErrorMessage(t: TFunction, errorCode: number): string {
  const translationKey = WABA_ERROR_TRANSLATION_KEYS[errorCode];
  if (translationKey) {
    return t(translationKey);
  }

  return t("waba_errors.generic_error", { errorCode });
}
